import AnswerForm from "./AnswerForm";
import { Answer } from "../../types/Answer";
import { Question } from "../../types/Question";
import UserAvatar from "../generic/UserAvatar";
import UserName from "../generic/UserName";
import IconText from "../generic/IconText";
import VoteDisplay from "../generic/VoteDisplay";
import BasicMenu from "../generic/BasicMenu";
import { Dictionary } from "../../../common/utils/types";
import { useAuth } from "../../contexts/auth.context";
import {
    useDeleteAnswerMutation,
    useUpdateAnswerMutation,
    useVoteAnswerMutation,
    useAcceptAnswerMutation,
} from "../../../features/api/answerSlice";

import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import DoneIcon from "@mui/icons-material/Done";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

interface AnswerItemProps {
    answer: Answer;
    question: Question;
}

const AnswerItem: React.FC<AnswerItemProps> = ({ answer, question }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [editMode, setEditMode] = useState<boolean>(false);
    const [localAnswer, setLocalAnswer] = useState<Answer>(answer);
    const author = answer.author;

    const [deleteAnswer, { isLoading: loadingDeleteAnswer }] = useDeleteAnswerMutation();
    const [updateAnswer, { isLoading: loadingUpdateAnswer }] = useUpdateAnswerMutation();
    const [voteAnswer, { isLoading: loadingVoteAnswer }] = useVoteAnswerMutation();
    const [acceptAnswer, { isLoading: loadingAcceptAnswer }] = useAcceptAnswerMutation();

    async function handleDelete() {
        if (!loadingDeleteAnswer) {
            try {
                console.log("Deleting answer");
                await deleteAnswer(answer).unwrap();
            } catch (error) {
                console.log(error);
            }
        }
    }

    function handleEdit() {
        setLocalAnswer(answer);
        setEditMode(true);
    }

    function handleEditCancel() {
        setLocalAnswer(answer);
        setEditMode(false);
    }

    async function handleEditSubmit() {
        console.log("Submiting edited answer");
        if (!loadingUpdateAnswer) {
            try {
                await updateAnswer(localAnswer).unwrap();
                setEditMode(false);
            } catch (error) {
                console.log(error);
            }
        }
    }

    async function handleVote(vote: number) {
        if (user.id === -1) {
            navigate("/signin");
            return;
        }
        if (!loadingVoteAnswer) {
            try {
                await voteAnswer({ answer, vote }).unwrap();
            } catch (error) {
                console.log(error);
            }
        }
    }

    function handleAccept(accept: boolean) {
        if (!loadingAcceptAnswer) {
            try {
                acceptAnswer({ ...answer, accepted: accept });
            } catch (error) {
                console.log(error);
            }
        }
    }

    const options: Dictionary<() => void> = {};

    if (user.id === author.id) {
        options.Edit = handleEdit;
        options.Delete = handleDelete;
    }

    if (user.id === question.author.id) {
        if (question.acceptedAnswer === -1) {
            options.Accept = () => handleAccept(true);
        } else if (answer.accepted) {
            options.Unaccept = () => handleAccept(false);
        }
    }

    const answerContent = (
        <Typography variant="body1" sx={{ fontSize: 18, whiteSpace: "pre-wrap" }}>
            {answer.body}
        </Typography>
    );

    return (
        <Card sx={{ display: "grid" }}>
            <CardHeader
                avatar={<UserAvatar user={author} size={20} />}
                action={<BasicMenu options={options} disabled={editMode} />}
                title={<UserName user={author} />}
                subheader={answer.createdAt}
                sx={{ textAlign: "left", paddingBottom: 0 }}
            />
            <CardContent sx={{ textAlign: "left", paddingLeft: 2, paddingBottom: 0 }}>
                {editMode ? (
                    <AnswerForm
                        answer={localAnswer}
                        onAnswerChange={setLocalAnswer}
                        onSubmit={handleEditSubmit}
                        onCancel={handleEditCancel}
                    />
                ) : (
                    answerContent
                )}
            </CardContent>
            <CardActions sx={{ display: "grid", p: 1 }}>
                {/* votes, accepted */}
                <Stack direction={"row"} spacing={3} color={"dimgray"} alignItems={"center"} p={1}>
                    <VoteDisplay votes={answer.votes} currentVote={answer.userVote} onVote={handleVote} />
                    {answer.accepted && <IconText icon={<DoneIcon color="success" />} text={"Accepted"} />}
                </Stack>
            </CardActions>
        </Card>
    );
};

export default AnswerItem;
